import { forwardRef, type ReactEventHandler } from 'react';

import {
  type AriaLabelingProps,
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import type {
  IntentPayload,
  SendIntentOptions,
  SendIntentWithResponseOptions,
} from '@dynatrace-sdk/navigation';

import { Item } from './Item.js';
import { ItemIcon } from './ItemIcon.js';
import { useIntent, useIntentWithResponse } from '../../core/hooks/intent.js';
import { AppIcon } from '../../layouts/app-header/app-icon/AppIcon.js';

/**
 * @public
 */
export interface MenuIntentBaseProps
  extends WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  disabled?: boolean;
  payload: IntentPayload;
  onSelect?: ReactEventHandler;
}

/**
 * @public
 */
export interface SuccessResponse {
  status: 'success';
  data: Record<string, unknown>;
}

/**
 * @public
 */
export interface ErrorResponse {
  status: 'error';
  error: Error;
}

/**
 * @public
 */
export interface MenuIntentWithResponseProps extends MenuIntentBaseProps {
  options: SendIntentWithResponseOptions<string>;
  onResponse: (response: SuccessResponse | ErrorResponse) => void;
}

/**
 * @public
 */
export interface MenuIntentWithoutResponseProps extends MenuIntentBaseProps {
  options: SendIntentOptions<IntentPayload>;
  onResponse?: never;
}

function isWithResponse(
  props: MenuIntentWithResponseProps | MenuIntentWithoutResponseProps,
): props is MenuIntentWithResponseProps {
  return 'onResponse' in props;
}

const IntentWithoutResponse = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuIntentWithoutResponseProps
>((props, ref) => {
  const { onSelect, payload, options, children, ...rest } = props;

  const intent = useIntent({
    payload,
    options,
  });

  return (
    <Item
      {...rest}
      ref={ref}
      onSelect={(e) => {
        intent.send();
        onSelect?.(e);
      }}
    >
      {intent.iconURL ? (
        <ItemIcon>
          <AppIcon src={intent.iconURL} alt="" />
        </ItemIcon>
      ) : null}
      {children}
    </Item>
  );
});

const IntentWithResponse = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuIntentWithResponseProps
>((props, ref) => {
  const { onSelect, onResponse, payload, options, children, ...rest } = props;

  const intent = useIntentWithResponse({
    payload,
    options,
  });

  const handleSelect: ReactEventHandler = async (e) => {
    onSelect?.(e);

    try {
      const response = await intent.send();
      onResponse(response);
    } catch {
      onResponse({
        status: 'error',
        error: new Error('Error sending intent with response'),
      });
    }
  };

  return (
    <Item {...rest} ref={ref} onSelect={handleSelect}>
      {intent.iconURL ? (
        <ItemIcon>
          <AppIcon src={intent.iconURL} alt="" />
        </ItemIcon>
      ) : null}
      {children}
    </Item>
  );
});

/**
 * @public
 */
export type MenuIntentProps =
  | MenuIntentWithResponseProps
  | MenuIntentWithoutResponseProps;

/**
 * A menu subcomponent to send intents.
 * @public
 */
export const Intent = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  MenuIntentProps
>((props, ref) => {
  if (isWithResponse(props)) {
    return <IntentWithResponse {...props} ref={ref} />;
  }
  return <IntentWithoutResponse {...props} ref={ref} />;
});

(Intent as typeof Intent & { displayName: string }).displayName = 'Menu.Intent';
